import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import tracker from './utils/tracker';
import AdminDashboard from './components/AdminDashboard';
import InvitationPage from './components/InvitationPage';
import InvitationLetter from './components/InvitationLetter';
import LandingPage from './components/LandingPage';
import ChatPage from './pages/ChatPage';
import UserDashboard from './components/UserDashboard';
import HiddenLogin from './components/HiddenLogin';
import AboutPage from './components/AboutPage';
import ContactPage from './components/ContactPage';
import JourneyPage from './components/JourneyPage';
import BlogList from './components/BlogList';
import BlogPost from './components/BlogPost';
import SecretMessage from './components/SecretMessage';
import SchoolSupplies from './components/SchoolSupplies';
import KingsCrossPage from './components/KingsCrossPage';
import GiftInvitation from './components/GiftInvitation';
import FAQPage from './components/FAQPage';
import SupportPage from './components/SupportPage';
import BannedPage from './pages/BannedPage';
import NotFoundPage from './pages/NotFoundPage';
import { ConfigProvider } from 'antd';
import faIR from 'antd/locale/fa_IR';
import { useTranslation } from 'react-i18next';
import { isRTL } from './i18n/config';

const HIDDEN_LOGIN_PATH = '/portal-9-3-4';

// Protected Admin Route
function ProtectedAdminRoute({ children }) {
  const token = Cookies.get('token');
  const admin = Cookies.get('admin');

  if (!token || !admin) {
    return <Navigate to={HIDDEN_LOGIN_PATH} replace />;
  }

  return children;
}

// دسترسی به داشبورد فقط نیازمند ورود (توکن) است — رسیدن به مدرسه دیگر شرط
// نمایش داشبورد نیست. راهنمایی برای عبور از سکو و سوار شدن قطار به‌صورت
// بنر داخل خود داشبورد نمایش داده می‌شود (UserDashboard.jsx) تا کاربر همیشه
// بتواند هر وقت خواست داشبورد را ببیند و مراحل سفر را در زمان خودش طی کند.
function ProtectedUserRoute({ children }) {
  const token = Cookies.get('token');

  if (!token) {
    return <Navigate to={HIDDEN_LOGIN_PATH} replace />;
  }

  return children;
}

// هر بار مسیر (SPA route) عوض شود یک pageview ثبت می‌شود — چون این یک
// Single Page App است، تغییر مسیر باعث reload واقعی مرورگر نمی‌شود، پس باید
// دستی روی location گوش بدهیم (window.onload به‌تنهایی کافی نیست).
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    tracker.trackPageview(location.pathname + location.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);
  return null;
}

function UserDashboardPage() {
  const logout = () => {
    Cookies.remove('token');
    window.location.href = HIDDEN_LOGIN_PATH;
  };

  return <UserDashboard onLogout={logout} />;
}

// پنل مدیریت (و صفحه ورود مخفی آن) همیشه محتوایش فارسی است، صرف‌نظر از
// این‌که تشخیص زبان سایت عمومی (بر اساس دامنه/IP/localStorage) چه چیزی
// برگردانده. بدون این override، اگر ادمین از خارج ایران وارد شود یا
// localStorage.language چیزی غیر از fa داشته باشد، کل پنل (منو، فونت،
// جهت متن) به LTR/انگلیسی سوییچ می‌کند در حالی که برچسب‌های منو همچنان
// فارسی هستند — باعث چپ‌چین‌شدن نامرتب پنل می‌شود.
const ADMIN_ROUTE_PREFIXES = ['/admin', HIDDEN_LOGIN_PATH];
const isAdminPath = (pathname) => ADMIN_ROUTE_PREFIXES.some(
  (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
);

function AppConfigProvider({ children }) {
  const { i18n } = useTranslation();
  const location = useLocation();
  const forceAdminLocale = isAdminPath(location.pathname);

  const lang = forceAdminLocale ? 'fa' : (i18n.language || 'fa');
  const direction = forceAdminLocale ? 'rtl' : (isRTL(lang) ? 'rtl' : 'ltr');
  const locale = lang === 'fa' ? faIR : undefined;
  const fontFamily = lang === 'fa'
    ? "'Vazirmatn', 'Segoe UI', Tahoma, sans-serif"
    : "'Inter', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', Tahoma, sans-serif";

  // چون <html dir>/lang توسط i18n/config.js بر اساس زبان کلی سایت تنظیم
  // می‌شود، در صفحات ادمین آن را دستی به rtl/fa برمی‌گردانیم.
  useEffect(() => {
    if (forceAdminLocale && typeof document !== 'undefined') {
      document.documentElement.dir = 'rtl';
      document.documentElement.lang = 'fa';
    }
  }, [forceAdminLocale]);

  return (
    <ConfigProvider
      locale={locale}
      direction={direction}
      theme={{
        token: {
          fontFamily,
          colorPrimary: '#722f37',
          colorLink: '#722f37',
          borderRadius: 8,
          fontSize: 14,
        },
        components: {
          Menu: { darkItemBg: '#1a1a2e', darkSubMenuItemBg: '#16213e', darkItemSelectedBg: '#722f37' },
          Layout: { headerBg: '#ffffff', siderBg: '#1a1a2e' },
          Card: { borderRadiusLG: 10 },
        },
      }}
    >
      {children}
    </ConfigProvider>
  );
}

export default function App() {
  return (
    <Router>
      <AppConfigProvider>
        <RouteTracker />
        <Routes>
          {/* Landing Page */}
          <Route path="/" element={<LandingPage />} />

          {/* Hidden Login Route */}
          <Route path={HIDDEN_LOGIN_PATH} element={<HiddenLogin />} />

          {/* Admin Routes */}
          <Route path="/admin/login" element={<Navigate to={HIDDEN_LOGIN_PATH} replace />} />
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedAdminRoute>
                <AdminDashboard />
              </ProtectedAdminRoute>
            }
          />

          {/* User Routes */}
          <Route path="/login" element={<Navigate to={HIDDEN_LOGIN_PATH} replace />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedUserRoute>
                <UserDashboardPage />
              </ProtectedUserRoute>
            }
          />

          {/* Public Routes */}
          <Route path="/invitation" element={<InvitationLetter />} />
          <Route path="/invitation/register" element={<InvitationPage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/story" element={<AboutPage />} />
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/journey" element={<JourneyPage />} />
          <Route path="/join" element={<JourneyPage />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:id" element={<BlogPost />} />
          <Route path="/secret" element={<SecretMessage />} />
          <Route path="/school-supplies" element={<SchoolSupplies />} />
          <Route path="/kings-cross" element={<KingsCrossPage />} />
          <Route path="/gift-invitation" element={<GiftInvitation />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/banned" element={<BannedPage />} />

          {/* هر مسیری که با هیچ‌کدام از موارد بالا مطابقت نداشته باشد اینجا
              می‌افتد — باید همیشه آخرین Route باشد. */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </AppConfigProvider>
    </Router>
  );
}
